import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link as RouterLinkDefault } from 'react-router-dom';

type InjectLinkOptions = {
    /** Tag used in the text as a placeholder wrapper, e.g. <link-to-brief>…</link-to-brief> */
    placeholderTag?: string;
    /** Destination URL for the link */
    href: string;
    /** Render Router link (true) or plain <a> (false) */
    useRouter?: boolean;
    /** Router link component to use (defaults to react-router-dom's Link) */
    RouterLinkComponent?: React.ComponentType<any>;
    /** Optional styling/class/attrs applied to the link */
    className?: string;
    style?: CSSProperties;
    target?: string;
    rel?: string;
    /** Key prefix for generated nodes */
    keyPrefix?: string;
};

/**
 * Replaces <placeholderTag>…</placeholderTag> ranges inside `text` with links.
 * If `href` is falsy, the tags are stripped and inner text is kept.
 */
export function injectLink(
    text: string,
    {
        placeholderTag = 'link',
        href,
        useRouter = true,
        RouterLinkComponent = RouterLinkDefault,
        className,
        style = { color: '#b75332', textDecoration: 'underline' },
        target,
        rel,
        keyPrefix = 'injected-link',
    }: InjectLinkOptions
): ReactNode {
    if (!text) return <></>;

    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const tag = esc(placeholderTag);
    const regex = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'g');

    if (!regex.test(text)) return <>{text}</>;
    regex.lastIndex = 0;

    const nodes: ReactNode[] = [];
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
        const [full, inner] = match;
        const start = match.index;
        if (start > last) nodes.push(text.slice(last, start));
        if (href) {
            if (useRouter) {
                nodes.push(
                    <RouterLinkComponent
                        key={`${keyPrefix}-${start}`}
                        to={href}
                        className={className}
                        style={style}
                        target={target}
                        rel={rel}
                    >
                        {inner}
                    </RouterLinkComponent>
                );
            } else {
                nodes.push(
                    <a
                        key={`${keyPrefix}-${start}`}
                        href={href}
                        className={className}
                        style={style}
                        target={target}
                        rel={rel}
                    >
                        {inner}
                    </a>
                );
            }
        } else {
            nodes.push(inner);
        }
        last = start + full.length;
    }
    if (last < text.length) nodes.push(text.slice(last));
    return <>{nodes}</>;
}
